import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from '@/components/ui/breadcrumb';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter
} from '@/components/ui/card';
import Error from '@/components/common/Error';
import { Form } from '@/components/ui/form';
import SpinnerButton from '@/components/common/SpinnerButton';

import FormGeneral from './components/common/FormGeneral';
import { useLocations, usePostLocation } from '@/data/location';

const formSchema = z.object({
  name: z.string().min(1),
  regionId: z.string().min(1),
  address: z.string().optional(),
  lat: z.number(),
  lng: z.number(),
  activities: z
    .array(z.string())
    .min(1, { message: 'Must select at least 1 activity' })
});

const AddPage = () => {
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const postLocation = usePostLocation();
  const locations = useLocations();
  const navigate = useNavigate();

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: '',
      regionId: '',
      address: '',
      lat: 49.888326,
      lng: -119.471581,
      activities: []
    }
  });

  const onSubmit = async (values) => {
    try {
      setSubmitting(true);
      setApiError('');
      const newLocation = await postLocation(values);
      locations.mutate((currentLocations) => [
        ...currentLocations,
        newLocation
      ]);
      setSubmitting(false);
      form.reset();
      navigate('/location');
    } catch (err) {
      setApiError(JSON.stringify(err.message));
      setSubmitting(false);
    }
  };

  return (
    <>
      <Breadcrumb className="mb-4">
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink asChild>
              <Link to="/location">Locations</Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>Add Location</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <Card>
        <CardHeader>
          <CardTitle>Add Location</CardTitle>
          <CardDescription>
            Add a location that your coaches can coach at.
          </CardDescription>
        </CardHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)}>
            <div className="mx-auto max-w-120">
              <CardContent className="">
                <div className="space-y-4">
                  <FormGeneral />
                  {apiError && <Error error={apiError} />}
                </div>
              </CardContent>
              <CardFooter className="flex justify-end">
                <SpinnerButton loading={submitting}>Add Location</SpinnerButton>
              </CardFooter>
            </div>
          </form>
        </Form>
      </Card>
    </>
  );
};

export default AddPage;
